/*
==========================================================
BerryHome Product Gallery Controls
Sprint 21A.1 — Stability Fix
Version: 1.0.1

- opravuje přepínání velkými i spodními šipkami
- zachovává miniatury a crossfade
- stabilizuje spodní počítadlo
- odstraňuje krátký focus záblesk spodních ovladačů
==========================================================
*/

(() => {
  const SELECTOR = 'media-gallery';

  function initGallery(gallery) {
    if (!gallery || gallery.dataset.bhBottomControlsReady === 'true') return;

    const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');

    if (!thumbnails || !viewer) return;

    const thumbnailItems = () =>
      Array.from(thumbnails.querySelectorAll('[data-target]')).filter(
        (item) => !item.classList.contains('thumbnail-list_item--variant')
      );

    const bottomControls = viewer.querySelector('.slider-buttons');
    const currentCounter = viewer.querySelector('.slider-counter--current');
    const totalCounter = viewer.querySelector('.slider-counter--total');

    const nativePreviousButton = viewer.querySelector(
      '.slider-buttons .slider-button--prev'
    );
    const nativeNextButton = viewer.querySelector(
      '.slider-buttons .slider-button--next'
    );

    const galleryPreviousButton = gallery.querySelector(
      '[data-bh-gallery-prev]'
    );
    const galleryNextButton = gallery.querySelector(
      '[data-bh-gallery-next]'
    );

    function getActiveIndex(items = thumbnailItems()) {
      const index = items.findIndex((item) =>
        item.querySelector('button[aria-current="true"]')
      );

      return index >= 0 ? index : 0;
    }

    function stabilizeBottomControls() {
      if (!bottomControls) return;

      const controlsRect = bottomControls.getBoundingClientRect();
      const counter = bottomControls.querySelector('.slider-counter');
      const counterRect = counter?.getBoundingClientRect();

      if (controlsRect.height > 0) {
        bottomControls.style.minHeight = `${Math.ceil(controlsRect.height)}px`;
      }

      if (counter && counterRect?.width > 0) {
        counter.style.minWidth = `${Math.ceil(counterRect.width)}px`;
      }
    }

    function syncCounter(forcedIndex = null) {
      const items = thumbnailItems();
      if (!items.length) return;

      const activeIndex =
        Number.isInteger(forcedIndex) ? forcedIndex : getActiveIndex(items);

      if (currentCounter) currentCounter.textContent = String(activeIndex + 1);
      if (totalCounter) totalCounter.textContent = String(items.length);
    }

    function clearCrossfade() {
      viewer
        .querySelectorAll('.bh-product-gallery-crossfade')
        .forEach((overlay) => overlay.remove());

      viewer
        .querySelectorAll('.bh-product-media-crossfade-in')
        .forEach((mediaItem) =>
          mediaItem.classList.remove('bh-product-media-crossfade-in')
        );
    }

    function prepareCrossfade(thumbnailItem) {
      const nextMediaId = thumbnailItem?.dataset?.target;
      if (!nextMediaId) return null;

      const currentMedia = viewer.querySelector(
        '.product__media-item.is-active'
      );
      const nextMedia = viewer.querySelector(
        `[data-media-id="${nextMediaId}"]`
      );

      if (!currentMedia || !nextMedia || currentMedia === nextMedia) {
        return null;
      }

      const currentVisual =
        currentMedia.querySelector('.product__media') ||
        currentMedia.firstElementChild;

      if (!currentVisual) return null;

      clearCrossfade();

      const viewerRect = viewer.getBoundingClientRect();
      const mediaRect = currentMedia.getBoundingClientRect();

      const overlay = document.createElement('div');
      overlay.className = 'bh-product-gallery-crossfade';
      overlay.setAttribute('aria-hidden', 'true');

      overlay.style.top = `${mediaRect.top - viewerRect.top}px`;
      overlay.style.left = `${mediaRect.left - viewerRect.left}px`;
      overlay.style.width = `${mediaRect.width}px`;
      overlay.style.height = `${mediaRect.height}px`;

      const visualClone = currentVisual.cloneNode(true);

      visualClone
        .querySelectorAll(
          'button, a, video, iframe, model-viewer, script, .loading__spinner'
        )
        .forEach((element) => {
          if (element.matches('video, iframe, model-viewer, script')) {
            element.remove();
          } else {
            element.setAttribute('tabindex', '-1');
          }
        });

      overlay.appendChild(visualClone);
      viewer.appendChild(overlay);

      nextMedia.classList.add('bh-product-media-crossfade-in');

      return {
        overlay,
        nextMedia,
      };
    }

    function runCrossfade(state) {
      if (!state) return;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          state.overlay.classList.add('is-leaving');
          state.nextMedia.classList.add('is-entering');
        });
      });

      window.setTimeout(() => {
        state.overlay.remove();
        state.nextMedia.classList.remove(
          'bh-product-media-crossfade-in',
          'is-entering'
        );
      }, 260);
    }

    function activateThumbnail(thumbnailItem) {
      if (!thumbnailItem) return;

      const items = thumbnailItems();
      const targetIndex = items.indexOf(thumbnailItem);
      const thumbnailButton = thumbnailItem.querySelector('button');

      if (!thumbnailButton || targetIndex < 0) return;

      const crossfadeState = prepareCrossfade(thumbnailItem);

      // Počítadlo nastavíme ještě před Shopify přepočtem.
      syncCounter(targetIndex);

      thumbnailButton.click();
      runCrossfade(crossfadeState);

      window.requestAnimationFrame(() => {
        syncCounter(targetIndex);
      });
    }

    function navigate(direction) {
      const items = thumbnailItems();
      if (items.length < 2) return;

      const currentIndex = getActiveIndex(items);
      const nextIndex =
        (currentIndex + direction + items.length) % items.length;

      activateThumbnail(items[nextIndex]);
    }

    function bindControl(button, direction) {
      if (!button || button.dataset.bhGalleryBound === 'true') return;

      button.dataset.bhGalleryBound = 'true';

      button.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse') {
            event.preventDefault();
          }
        },
        true
      );

      button.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          navigate(direction);

          window.requestAnimationFrame(() => {
            if (button.matches(':focus')) button.blur();
          });
        },
        true
      );

      button.removeAttribute('disabled');
      button.setAttribute('aria-disabled', 'false');
    }

    bindControl(galleryPreviousButton, -1);
    bindControl(galleryNextButton, 1);
    bindControl(nativePreviousButton, -1);
    bindControl(nativeNextButton, 1);

    thumbnails.addEventListener(
      'click',
      (event) => {
        const thumbnailButton = event.target.closest('button');
        const thumbnailItem = thumbnailButton?.closest('[data-target]');

        if (thumbnailItem) {
          const crossfadeState = prepareCrossfade(thumbnailItem);

          window.requestAnimationFrame(() => {
            runCrossfade(crossfadeState);
            syncCounter();
          });
        }
      },
      true
    );

    const observer = new MutationObserver((mutations) => {
      const activeStateChanged = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-current'
      );

      if (activeStateChanged) {
        window.requestAnimationFrame(syncCounter);
      }
    });

    thumbnails.querySelectorAll('button').forEach((button) => {
      observer.observe(button, {
        attributes: true,
        attributeFilter: ['aria-current'],
      });
    });

    gallery.dataset.bhBottomControlsReady = 'true';

    stabilizeBottomControls();
    syncCounter();

    window.requestAnimationFrame(stabilizeBottomControls);
  }

  function initAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();
